import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiCheckCircle, FiAlertCircle, FiDollarSign, FiInfo } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const MPConfigCard = ({ userType }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [store, setStore] = useState(null);
  const [config, setConfig] = useState({
    mp_access_token: '',
    has_mei: false
  });
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      if (userType === 'lojista') {
        const [statsRes, storeRes] = await Promise.all([
          api.get('/stats/store'),
          api.get('/stores/my')
        ]);
        setStats(statsRes.data);
        setStore(storeRes.data);
        setConfig({
          mp_access_token: '',
          has_mei: storeRes.data.has_mei || false
        });
      } else {
        const statsRes = await api.get('/stats/delivery');
        setStats(statsRes.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config.mp_access_token && !stats?.mp_configured) {
      alert('Por favor, insira o Access Token do Mercado Pago');
      return;
    }

    setSaving(true);
    try {
      const endpoint = userType === 'lojista' ? '/stores/my/mp-config' : '/auth/mp-config';
      await api.put(endpoint, {
        mp_access_token: config.mp_access_token || undefined,
        has_mei: config.has_mei
      });
      alert('Configurações salvas com sucesso!');
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-4 text-white">
        <div className="flex items-center gap-3">
          <FiCreditCard className="text-2xl" />
          <div>
            <h3 className="font-semibold">Configuração de Pagamentos</h3>
            <p className="text-sm text-blue-100">Mercado Pago</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Status */}
        <div className={`p-4 rounded-xl flex items-center gap-3 ${stats?.mp_configured ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          {stats?.mp_configured ? (
            <>
              <FiCheckCircle className="text-2xl text-green-600" />
              <div>
                <p className="font-medium text-green-800">Mercado Pago Configurado</p>
                <p className="text-sm text-green-600">Seus pagamentos serão processados automaticamente</p>
              </div>
            </>
          ) : (
            <>
              <FiAlertCircle className="text-2xl text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Configuração Pendente</p>
                <p className="text-sm text-yellow-600">Configure seu Mercado Pago para receber pagamentos</p>
              </div>
            </>
          )}
        </div>

        {/* Wallet Balance */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FiDollarSign className="text-green-600" />
              <span className="text-gray-600">Saldo na Carteira</span>
            </div>
            <span className="text-2xl font-bold text-green-600">{formatCurrency(stats?.wallet_balance || 0)}</span>
          </div>
          {!stats?.has_mei && stats?.wallet_balance > 0 && (
            <p className="text-xs text-gray-500 mt-2">Sem MEI, os valores ficam acumulados aqui</p>
          )}
        </div>

        {/* MEI Status */}
        <div className="space-y-2">
          <label className="flex items-center gap-3 p-4 border rounded-xl cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={config.has_mei}
              onChange={e => setConfig({...config, has_mei: e.target.checked})}
              className="w-5 h-5 text-blue-600 rounded"
            />
            <div>
              <p className="font-medium">Tenho MEI/CNPJ</p>
              <p className="text-sm text-gray-500">Pagamentos serão transferidos automaticamente</p>
            </div>
          </label>
        </div>

        {/* Access Token */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Access Token do Mercado Pago
          </label>
          <div className="relative">
            <input
              type={showToken ? 'text' : 'password'}
              value={config.mp_access_token}
              onChange={e => setConfig({...config, mp_access_token: e.target.value})}
              placeholder={stats?.mp_configured ? '••••••••••••••••' : 'APP_USR-...'}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-blue-600"
            >
              {showToken ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            {stats?.mp_configured ? 'Deixe em branco para manter o token atual' : 'Encontre em: Mercado Pago → Seu negócio → Configurações → Credenciais'}
          </p>
        </div>

        {/* Info box for non-MEI */}
        {!config.has_mei && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex gap-3">
              <FiInfo className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800">Sem MEI?</p>
                <p className="text-sm text-blue-600">Seus ganhos ficarão acumulados na carteira. Você poderá solicitar saque quando atingir o valor mínimo.</p>
              </div>
            </div>
          </div>
        )}

        {/* Lojista specific - Monthly info */}
        {userType === 'lojista' && stats && (
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <h4 className="font-medium text-gray-700">Status da Mensalidade</h4>
            <div className="flex justify-between">
              <span className="text-gray-600">Comissões este mês:</span>
              <span className="font-semibold">{formatCurrency(stats.monthly_commissions)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Meta para isenção:</span>
              <span className="font-semibold">{formatCurrency(stats.monthly_fee)}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all"
                style={{width: `${Math.min(100, (stats.monthly_commissions / stats.monthly_fee) * 100)}%`}}
              />
            </div>
            {stats.monthly_commissions >= stats.monthly_fee ? (
              <p className="text-sm text-green-600 font-medium">✓ Isento da mensalidade este mês!</p>
            ) : (
              <p className="text-sm text-gray-500">Faltam {formatCurrency(stats.remaining_fee)} em comissões para isenção</p>
            )}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Salvando...' : 'Salvar Configurações'}
        </button>
      </div>
    </div>
  );
};

export default MPConfigCard;
